import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../../core/apiClient.ts';

type Batch = { id: string; status: string; created_at: string; scheduled_at: string | null; launches_count: number; success_count: number; error_count: number; template_name?: string };
type Launch = { id: number; ad_account_id: string; status: string; error_message: string | null };
type Log = { step: string; method: string; url: string; request_body: string; response_status: number; response_body: string; created_at: string };

function formatLog(logs: Log[]): string {
  return logs
    .map((log) => {
      let req = '';
      try { req = JSON.stringify(JSON.parse(log.request_body), null, 2); } catch { req = log.request_body; }
      let res = '';
      try { res = JSON.stringify(JSON.parse(log.response_body), null, 2); } catch { res = log.response_body; }
      return `${log.step} · ${log.response_status}\n${req}\n${res}`;
    })
    .join('\n\n---\n\n');
}

export function QueuePage() {
  const location = useLocation();
  const successMessage = (location.state as { message?: string } | null)?.message;

  const [batches, setBatches] = useState<Batch[]>([]);
  const [launches, setLaunches] = useState<Launch[]>([]);
  const [open, setOpen] = useState('');
  const [logs, setLogs] = useState<Log[] | null>(null);
  const [copied, setCopied] = useState(false);
  const [activeLaunchId, setActiveLaunchId] = useState<number | null>(null);

  const load = () => api.get<Batch[]>('/api/launcher/batches').then(setBatches);

  useEffect(() => {
    load();
    const i = setInterval(() => {
      load();
      if (open) api.get<Launch[]>(`/api/launcher/batches/${open}/launches`).then(setLaunches).catch(() => {});
      if (activeLaunchId) api.get<Log[]>(`/api/launcher/launches/${activeLaunchId}/logs`).then(setLogs).catch(() => {});
    }, 5000);
    return () => clearInterval(i);
  }, [open, activeLaunchId]);

  const toggle = async (batch: Batch) => {
    const next = open === batch.id ? '' : batch.id;
    setOpen(next);
    if (next) setLaunches(await api.get<Launch[]>(`/api/launcher/batches/${batch.id}/launches`));
  };

  const copyLogs = async () => {
    if (!logs) return;
    try {
      await navigator.clipboard.writeText(formatLog(logs));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback for older browsers
      const el = document.createElement('textarea');
      el.value = formatLog(logs);
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatScheduled = (batch: Batch) => {
    if (!batch.scheduled_at) return null;
    try {
      return new Date(batch.scheduled_at).toLocaleString();
    } catch {
      return batch.scheduled_at;
    }
  };

  return (
    <div className="page-body">
      <div className="templates-head">
        <div>
          <h1>Launch queue</h1>
          <p className="text-muted text-sm">Batches and execution logs.</p>
        </div>
        <button className="btn btn--secondary" onClick={load}>Refresh</button>
      </div>

      {successMessage && (
        <div className="notification notification--success">{successMessage}</div>
      )}

      <table className="data-table">
        <thead>
          <tr>
            <th>Batch</th>
            <th>Template</th>
            <th>Status</th>
            <th>Scheduled</th>
            <th>Launches</th>
            <th>Success</th>
            <th>Errors</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {batches.map((batch) => (
            <React.Fragment key={batch.id}>
              <tr className={`queue-row queue-row--${batch.status}`}>
                <td className="font-mono" onClick={() => toggle(batch)} style={{ cursor: 'pointer' }}>{batch.id.slice(0, 8)}…</td>
                <td style={{ maxWidth: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={batch.template_name}>{batch.template_name ?? '—'}</td>
                <td>{batch.status}</td>
                <td className="text-muted text-sm">{formatScheduled(batch) ?? '—'}</td>
                <td>{batch.launches_count}</td>
                <td>{batch.success_count}</td>
                <td>{batch.error_count}</td>
                <td>
                  <button
                    className="btn btn--secondary btn--sm"
                    disabled={!['queued', 'running'].includes(batch.status)}
                    onClick={async () => { await api.post(`/api/launcher/batches/${batch.id}/stop`); load(); }}
                  >
                    Stop
                  </button>{' '}
                  <button
                    className="btn btn--secondary btn--sm"
                    disabled={!batch.error_count}
                    onClick={async () => { await api.post(`/api/launcher/batches/${batch.id}/retry`); load(); }}
                  >
                    Retry errors
                  </button>
                </td>
              </tr>
              {open === batch.id && (
                <tr>
                  <td colSpan={8}>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Account</th>
                          <th>Status</th>
                          <th>Error</th>
                          <th>Log</th>
                        </tr>
                      </thead>
                      <tbody>
                        {launches.map((launch) => (
                          <tr className={`queue-row queue-row--${launch.status}`} key={launch.id}>
                            <td>{launch.ad_account_id}</td>
                            <td>{launch.status}</td>
                            <td>{launch.error_message ?? ''}</td>
                            <td>
                              <button
                                className="btn btn--secondary btn--sm"
                                onClick={async () => { setCopied(false); setActiveLaunchId(launch.id); setLogs(await api.get<Log[]>(`/api/launcher/launches/${launch.id}/logs`)); }}
                              >
                                Log
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      {logs && (
        <div className="queue-drawer">
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
            <button className="btn btn--secondary" onClick={() => { setLogs(null); setActiveLaunchId(null); }}>Close</button>
            <button className="btn btn--secondary" onClick={copyLogs}>
              {copied ? '✓ Copied!' : '📋 Copy log'}
            </button>
          </div>
          <h2>Launch logs</h2>
          {logs.length ? logs.map((log, i) => (
            <details key={i} open>
              <summary>{log.step} · {log.response_status}</summary>
              <pre>{log.request_body}</pre>
              <pre>{log.response_body}</pre>
            </details>
          )) : <p>No logs for this launch.</p>}
        </div>
      )}
    </div>
  );
}
